
import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";

const PrivacyPolicy = () => {
  return (
    <div className="container py-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <Button 
          variant="outline" 
          asChild
          className="mb-4"
        >
          <Link to="/about-us">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to About Us
          </Link>
        </Button>
      
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold tracking-tight text-nalc-dark dark:text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground mb-8">
            Last updated: April 9, 2025
          </p>
        </motion.div>
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card className="bg-white dark:bg-slate-800 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl">Privacy Policy for Noor Ali Learning Center</CardTitle>
            
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger>Quick Navigation</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid gap-3 p-4 w-[300px]">
                      <li className="row-span-3">
                        <a href="#information-collection" className="flex h-full w-full select-none flex-col justify-end rounded-md bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 p-6 no-underline outline-none focus:shadow-md">
                          <div className="mb-2 mt-4 text-lg font-medium">
                            Skip to Section
                          </div>
                          <p className="text-sm leading-tight text-muted-foreground">
                            Quickly navigate to key sections of our privacy policy
                          </p>
                        </a>
                      </li>
                      <li>
                        <a href="#information-collection" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                          <div className="text-sm font-medium leading-none">
                            Information Collection
                          </div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            What data we collect and why
                          </p>
                        </a>
                      </li>
                      <li>
                        <a href="#data-usage" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                          <div className="text-sm font-medium leading-none">
                            Data Usage
                          </div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            How we use your information
                          </p>
                        </a>
                      </li>
                      <li>
                        <a href="#data-security" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                          <div className="text-sm font-medium leading-none">
                            Data Security
                          </div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            How we protect your information
                          </p>
                        </a>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </CardHeader>
          <CardContent className="prose dark:prose-invert max-w-none">
            <p>
              At Noor Ali Learning Center, accessible from our application, one of our main priorities 
              is the privacy of our visitors and students. This Privacy Policy document contains types 
              of information that is collected and recorded by Noor Ali Learning Center and how we use it.
            </p>
            
            <p>
              If you have additional questions or require more information about our Privacy Policy, 
              do not hesitate to contact us through the information provided at the bottom of this document.
            </p>
            
            <h3 id="information-collection">Information We Collect</h3>
            <p>
              The personal information that you are asked to provide, and the reasons why you are asked 
              to provide it, will be made clear to you at the point we ask you to provide your personal 
              information.
            </p>
            <p>
              If you contact us directly, we may receive additional information about you such as your 
              name, email address, phone number, the contents of the message and/or attachments you may 
              send us, and any other information you may choose to provide.
            </p>
            <p>
              When you register for an account, we may ask for your contact information, including items 
              such as name, company name, address, email address, and telephone number.
            </p>
            
            <h3 id="data-usage">How We Use Your Information</h3>
            <p>We use the information we collect in various ways, including to:</p>
            <ul>
              <li>Provide, operate, and maintain our application</li>
              <li>Improve, personalize, and expand our application</li>
              <li>Understand and analyze how you use our application</li>
              <li>Develop new products, services, features, and functionality</li>
              <li>Communicate with you, including for customer service</li>
              <li>Send you updates and other information related to our services</li>
              <li>Find and prevent fraud</li>
            </ul>
            
            <h3 id="data-security">Data Security</h3>
            <p>
              The security of your data is important to us, but remember that no method of transmission 
              over the Internet, or method of electronic storage is 100% secure. While we strive to use 
              commercially acceptable means to protect your personal data, we cannot guarantee its absolute 
              security.
            </p>
            
            <h3>Log Files</h3>
            <p>
              Noor Ali Learning Center follows a standard procedure of using log files. These files log visitors 
              when they use our application. The information collected by log files include internet protocol 
              (IP) addresses, browser type, Internet Service Provider (ISP), date and time stamp, referring/exit 
              pages, and possibly the number of clicks. These are not linked to any information that is personally 
              identifiable. The purpose of the information is for analyzing trends, administering the application, 
              tracking users' movement within the application, and gathering demographic information.
            </p>
            
            <h3>Cookies and Web Beacons</h3>
            <p>
              Like any other application, Noor Ali Learning Center uses "cookies". These cookies are used to store 
              information including visitors' preferences, and the pages on the application that the visitor 
              accessed or visited. The information is used to optimize the users' experience by customizing 
              our web page content based on visitors' browser type and/or other information.
            </p>
            
            <h3>Children's Information</h3>
            <p>
              Another part of our priority is adding protection for children while using the internet. We encourage 
              parents and guardians to observe, participate in, and/or monitor and guide their online activity.
            </p>
            <p>
              Noor Ali Learning Center does not knowingly collect any Personal Identifiable Information from children 
              under the age of 13. If you think that your child provided this kind of information on our application, 
              we strongly encourage you to contact us immediately and we will do our best efforts to promptly 
              remove such information from our records.
            </p>
            
            <h3>Third-Party Privacy Policies</h3>
            <p>
              Noor Ali Learning Center's Privacy Policy does not apply to other advertisers or websites. Thus, we are 
              advising you to consult the respective Privacy Policies of these third-party ad servers for more 
              detailed information. It may include their practices and instructions about how to opt-out of 
              certain options.
            </p>
            
            <h3>Changes to This Privacy Policy</h3>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page.
            </p>
            <p>
              You are advised to review this Privacy Policy periodically for any changes. Changes to this 
              Privacy Policy are effective when they are posted on this page.
            </p>
            
            <h3>Contact Us</h3>
            <p>
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <ul>
              <li>By email: <a href="mailto:www.mrdesigner.af@gmail.com" className="text-nalc-teal hover:underline">www.mrdesigner.af@gmail.com</a></li>
              <li>By phone: <a href="tel:+93784604196" className="text-nalc-teal hover:underline">+93 784604196</a></li>
              <li>By visiting this page on our website: <Link to="/about-us" className="text-nalc-teal hover:underline">About Us page</Link></li>
            </ul>
            
            <div className="mt-8 border-t pt-6">
              <Button asChild variant="outline" className="flex items-center gap-2">
                <Link to="/terms-conditions">
                  View Terms and Conditions <ExternalLink className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default PrivacyPolicy;
